import React, { useContext, useEffect, useState } from "react";
import {
  Card,
  Table,
  Button,
  Typography,
  Tag,
  Row,
  Col,
  Statistic,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Switch,
  message,
  Popconfirm,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  PercentageOutlined,
  ScheduleOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { AppContext } from "../context/AppContext";

const { Title, Text } = Typography;
const { Option } = Select;

const PRODUCT_TYPES = [
  { label: "Fixed Deposit", value: "fixed_deposit" },
  { label: "Flexible Deposit", value: "flexible_deposit" },
  { label: "Syndicated Investment", value: "syndicated" },
  { label: "Structured Product", value: "structured" },
  { label: "Fund-of-Funds", value: "fund_of_funds" },
];

const INTEREST_TYPES = [
  { label: "Simple", value: "simple" },
  { label: "Compound", value: "compound" },
];

const COMPOUND_FREQUENCIES = [
  { label: "Daily", value: "daily" },
  { label: "Weekly", value: "weekly" },
  { label: "Monthly", value: "monthly" },
  { label: "Quarterly", value: "quarterly" },
  { label: "Yearly", value: "yearly" },
];

const TERM_UNITS = [
  { label: "Days", value: "days" },
  { label: "Months", value: "months" },
  { label: "Years", value: "years" },
];

const RISK_RATINGS = [
  "A+",
  "A",
  "A-",
  "B+",
  "B",
  "B-",
  "C+",
  "C",
  "C-",
  "D",
].map((rating) => ({ label: rating, value: rating }));

const InvestmentProducts = () => {
  const { company } = useContext(AppContext);
  const token = localStorage.getItem("token");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    if (company?.id) fetchProducts();
  }, [company?.id]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `https://zedlenders.pickmesms.com/items/investment_products?filter[company]=${company.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProducts(res.data.data || []);
    } catch (error) {
      message.error("Failed to fetch investment products");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    try {
      await form.validateFields();

      const url = currentProduct
        ? `https://zedlenders.pickmesms.com/items/investment_products/${currentProduct.id}`
        : "https://zedlenders.pickmesms.com/items/investment_products";

      const method = currentProduct ? "patch" : "post";

      await axios[method](
        url,
        {
          ...values,
          company: company.id,
          status: "published",
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      message.success(
        `Product ${currentProduct ? "updated" : "created"} successfully`
      );
      setShowModal(false);
      setCurrentProduct(null);
      form.resetFields();
      fetchProducts();
    } catch (error) {
      if (error.errorFields) {
        message.error("Please complete all required fields.");
      } else {
        message.error("Operation failed. Please try again.");
      }
    }
  };

  const handleEdit = (record) => {
    setCurrentProduct(record);
    form.setFieldsValue(record);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    try {
      const usageResponse = await axios.get(
        `https://zedlenders.pickmesms.com/items/investments?filter[investment_product][_eq]=${id}&fields=id`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if ((usageResponse.data.data || []).length > 0) {
        message.error(
          "Cannot delete this product. There are existing investments using it."
        );
        return;
      }

      await axios.delete(
        `https://zedlenders.pickmesms.com/items/investment_products/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      message.success("Product deleted successfully");
      fetchProducts();
    } catch (error) {
      message.error("Delete operation failed. Please try again.");
    }
  };

  const columns = [
    {
      title: "Product",
      dataIndex: "name",
      render: (text, record) => (
        <div>
          <Text strong>{text}</Text>
          <br />
          <Text type="secondary">{record.description}</Text>
        </div>
      ),
    },
    {
      title: "Type",
      render: (_, record) => {
        const typeValue =
          record.product_type || record.interest_typon || record.interest_type;
        return (
          <Tag color="blue">
            {PRODUCT_TYPES.find((t) => t.value === typeValue)?.label ||
              typeValue ||
              "N/A"}
          </Tag>
        );
      },
    },
    {
      title: "Interest",
      render: (_, record) => (
        <>
          <Tag icon={<PercentageOutlined />} color="geekblue">
            {record.interest_rate}% {record.interest_type}
          </Tag>
          {record.interest_type === "compound" && record.compound_frequency && (
            <Tag color="purple">{record.compound_frequency}</Tag>
          )}
        </>
      ),
    },
    {
      title: "Term",
      render: (_, record) => (
        <Tag icon={<ScheduleOutlined />} color="green">
          {record.term_days} {record.term_unit}
        </Tag>
      ),
    },
    {
      title: "Risk",
      dataIndex: "risk_rating",
      render: (value) => (
        <Tag icon={<SafetyCertificateOutlined />} color="gold">
          {value || "N/A"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      render: (_, record) => (
        <>
          <Button
            icon={<EditOutlined />}
            size="small"
            style={{ marginRight: 8 }}
            onClick={() => handleEdit(record)}
          />
          <Popconfirm
            title="Delete this product?"
            onConfirm={() => handleDelete(record.id)}
          >
            <Button icon={<DeleteOutlined />} size="small" danger />
          </Popconfirm>
        </>
      ),
    },
  ];

  const totalProducts = products.length;

  return (
    <div className="page-container">
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} md={14}>
          <Title level={3} style={{ marginBottom: 0 }}>
            Investment Products
          </Title>
          <Text type="secondary">
            Configure investment offerings, pricing, and compliance rules
          </Text>
        </Col>
        <Col xs={24} md={10} style={{ textAlign: "right" }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setShowModal(true)}
          >
            New Product
          </Button>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic title="Total Products" value={totalProducts} />
          </Card>
        </Col>
      </Row>

      <Card>
        <Table
          columns={columns}
          dataSource={products}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Modal
        title={currentProduct ? "Edit Investment Product" : "Create Investment Product"}
        open={showModal}
        onCancel={() => {
          setShowModal(false);
          setCurrentProduct(null);
          form.resetFields();
        }}
        footer={null}
        width={900}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Product Name"
                name="name"
                rules={[{ required: true, message: "Please enter product name" }]}
              >
                <Input placeholder="e.g., 91-Day Fixed Deposit" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Product Type"
                name="product_type"
                rules={[{ required: true, message: "Select product type" }]}
              >
                <Select options={PRODUCT_TYPES} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Description" name="description">
            <Input.TextArea rows={2} />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Interest Rate (%)"
                name="interest_rate"
                rules={[{ required: true, message: "Enter interest rate" }]}
              >
                <InputNumber min={0} max={100} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Interest Type"
                name="interest_type"
                rules={[{ required: true, message: "Select interest type" }]}
              >
                <Select options={INTEREST_TYPES} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Compound Frequency" name="compound_frequency">
                <Select options={COMPOUND_FREQUENCIES} allowClear />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Term"
                name="term_days"
                rules={[{ required: true, message: "Enter term" }]}
              >
                <InputNumber min={1} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Term Unit"
                name="term_unit"
                rules={[{ required: true, message: "Select term unit" }]}
              >
                <Select options={TERM_UNITS} />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item label="Min Amount" name="min_amount">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item label="Max Amount" name="max_amount">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={6}>
              <Form.Item label="Lock-in (days)" name="lock_in_days">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Notice Period (days)" name="notice_period_days">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Early Withdrawal Penalty (%)" name="early_withdrawal_penalty_rate">
                <InputNumber min={0} max={100} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Withholding Tax (%)" name="withholding_tax_rate" initialValue={15}>
                <InputNumber min={0} max={50} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={6}>
              <Form.Item label="Risk Rating" name="risk_rating">
                <Select options={RISK_RATINGS} allowClear />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="KYC Required" name="kyc_required" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Accredited Only" name="accredited_required" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Max Invest % Net Worth" name="max_investment_percent">
                <InputNumber min={0} max={100} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Concentration Limit (%)" name="concentration_limit_percent">
                <InputNumber min={0} max={100} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Collateral Coverage Ratio" name="collateral_coverage_ratio">
                <InputNumber min={0} max={10} step={0.1} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Button type="primary" htmlType="submit" block>
              {currentProduct ? "Update Product" : "Create Product"}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default InvestmentProducts;
